import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { fetchOrg, updateOrg } from '../lib/organizations.js';
import OrgForm from '../components/organizations/OrgForm.jsx';

export default function OrganizationsEdit() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, isActive } = useAuth();
  const [org, setOrg] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // `t` deliberately excluded — see ScholarsEdit for the rationale.
  useEffect(() => {
    let cancelled = false;
    fetchOrg(id)
      .then((row) => !cancelled && (row ? setOrg(row) : setError(t('organizations.form.notFound'))))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!isActive) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <h1 className="cta-title">{t('bibliography.form.notActiveTitle')}</h1>
          <p className="cta-body">{t('bibliography.form.notActiveBody')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
      </div>
    );
  }
  if (!org) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  // Non-admins can't update directly (RLS); offer a polite block until we add
  // edit suggestions for orgs (not yet in scope).
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{t('organizations.form.adminOnly')}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <div className="section-label">{t('organizations.form.savedLabel')}</div>
          <h1 className="cta-title">{org.name}</h1>
          <p className="cta-body">{t('organizations.form.savedBody')}</p>
          <button type="button" className="btn-primary" onClick={() => navigate('/organizations')}>
            {t('organizations.form.backToList')}
          </button>
        </div>
      </div>
    );
  }

  async function handleSubmit(form) {
    setBusy(true);
    try {
      await updateOrg(org.id, form);
      setSubmitted(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 pb-20 pt-28">
      <header className="mb-8">
        <div className="section-label">{t('organizations.form.editLabel')}</div>
        <h1 className="font-serif text-3xl font-semibold text-cream">{org.name}</h1>
      </header>

      <OrgForm
        initialValue={org}
        submitLabel={t('organizations.form.save')}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/organizations')}
        busy={busy}
      />
    </div>
  );
}
